import { Button, CardActions, CardContent, Divider, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import {
	useCreateUserListMutation,
	useEditUserMutation,
	useGetUserDetailQuery,
} from '@/api/user-management/userManagementListApiSlice';
import {
	StaffSimpleDetail,
	StaffSimpleDetailDto,
} from '@/api/DTO/user-management/userManagementList.interface';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useGetAllEnumListQuery, useGetUnitListQuery } from '@/api/common';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { useGetUser } from '@/hooks/useGetUser';
import { PermissionEnum } from '@/api/enum/common.enum';

interface Props {
	type: FormType;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function UserInfoForm({ type, setType }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [createUserList] = useCreateUserListMutation();
	const [editUser] = useEditUserMutation();
	const { userId } = useParams();
	const { userInfo } = useGetUser();

	const { data: branchList }: any = useGetUnitListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: details } = useGetUserDetailQuery(userId || '', {
		refetchOnMountOrArgChange: true,
		skip: !userId,
	});

	const defaultValues: DefaultValues<StaffSimpleDetailDto> = {};
	// useEffect(() => {
	// 	if (type == FormType.edit) {
	// 		console.log(1);
	// 	}
	// }, [details]);

	const formSchema = Yup.object().shape({
		role: Yup.string().required('pleaseSelect'),
		branchIds: Yup.array().of(Yup.string()).required('pleaseSelect'),
		disabled: Yup.boolean().required('pleaseSelect'),
		name: Yup.string().required('pleaseEnter'),
		username: Yup.string().required('pleaseEnter'),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		const subscription = methods.watch((value, field) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	useEffect(() => {
		if (details) {
			methods.reset(details.data);
			const branchList = details.data?.branchList?.map((item: any) => {
				return item.id;
			});
			methods.setValue('branchIds', branchList || []);
		}
	}, [details]);

	const onSubmit: SubmitHandler<StaffSimpleDetailDto> = async (data) => {
		const obj: StaffSimpleDetailDto = { ...data };
		const response =
			type == FormType.create ? await createUserList(obj) : await editUser(obj);

		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createUserSuccess')
					: t('snackbar.updateUserSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate('/user-management/user-management-list');
			} else {
				navigate(`/user-management/user-management-list/detail/${userId}`);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) {
			navigate(`/user-management/user-management-list/detail/${userId}`);
		} else {
			navigate('/user-management/user-management-list');
		}
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='role'
									label={t('user.role') + '*'}
									options={(allEnumList?.data.StaffRoleEnum || []).map(
										(item: any) => ({
											...item,
											id: item.code,
											name: item.descLong,
										})
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppMultipleDropDownMenu
									control={methods.control}
									name='branchIds'
									label={t('user.unit') + '*'}
									options={branchList?.data.map((item: any) => {
										return {
											id: item.id,
											name: item.displayName,
										};
									})}
								/>
							</Grid>
							{type == FormType.edit ? (
								<Grid size={6}>
									<AppFieldView
										label={t('user.userId')}
										value={details?.data.username}
									/>
								</Grid>
							) : (
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='username'
										label={t('user.userId') + '*'}
									/>
								</Grid>
							)}

							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='name'
									label={t('user.name') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='post'
									label={t('preAdmission.post')}
								/>
							</Grid>
							{(type !== FormType.edit ||
								userInfo?.permissionList?.includes(
									PermissionEnum.SuspendSystemUserAccount
								)) && (
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='disabled'
										label={t('member.status') + '*'}
										options={[
											{ id: false, name: t('user.active') },
											{ id: true, name: t('user.suspended') },
										]}
									/>
								</Grid>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							endIcon={<CheckCircleOutlineIcon />}
						>
							{t('button.complete')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
